/**
 * Collection Report Page
 * Displays aging analysis and customer-wise collection report
 * US-INV-004: AC-4 - Collection Report
 */

import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Download, TrendingDown, BarChart3, Calendar } from 'lucide-react';
import { invoiceService } from '@/services/commerce/invoiceService';
import type { CollectionReport } from '@/types/invoice';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const COLORS = ['#10b981', '#f59e0b', '#f97316', '#ef4444'];

export const CollectionReportPage: React.FC = () => {
  const [report, setReport] = useState<CollectionReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadReport();
  }, []);

  const loadReport = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await invoiceService.getCollectionReport();
      setReport(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load collection report');
      console.error('Error loading collection report:', err);
      toast.error('Failed to load collection report');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format: 'pdf' | 'excel') => {
    try {
      toast.loading(`Exporting ${format.toUpperCase()}...`);
      await invoiceService.exportOverdueReport(format);
      toast.success(`Report exported successfully as ${format.toUpperCase()}`);
    } catch (err) {
      console.error('Export error:', err);
      toast.error(`Failed to export ${format.toUpperCase()}`);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="container mx-auto py-6">
        <Card>
          <CardContent className="flex items-center justify-center h-96">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="container mx-auto py-6">
        <Card>
          <CardContent className="flex flex-col items-center justify-center h-96 gap-4">
            <TrendingDown className="h-12 w-12 text-red-500" />
            <p className="text-red-600">{error || 'No data available'}</p>
            <Button onClick={loadReport}>Retry</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Prepare chart data
  const agingChartData = report.agingAnalysis.map((bucket) => ({
    name: bucket.range + ' days',
    amount: bucket.totalAmount,
    count: bucket.invoiceCount,
    percentage: bucket.percentage,
  }));

  const agingPieData = report.agingAnalysis.map((bucket) => ({
    name: bucket.range + ' days',
    value: bucket.totalAmount,
  }));

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <BarChart3 className="h-8 w-8 text-blue-600" />
            Collection Report
          </h1>
          <p className="text-gray-600 mt-1">Aging analysis and customer-wise breakdown</p>
          <p className="text-sm text-gray-500 flex items-center gap-2 mt-2">
            <Calendar className="h-4 w-4" />
            Generated on: {formatDate(report.generatedAt)}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => handleExport('pdf')} className="gap-2">
            <Download className="h-4 w-4" />
            Export PDF
          </Button>
          <Button variant="outline" onClick={() => handleExport('excel')} className="gap-2">
            <Download className="h-4 w-4" />
            Export Excel
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardDescription>Total Overdue Amount</CardDescription>
            <CardTitle className="text-3xl text-red-600">
              {formatCurrency(report.totalOverdueAmount)}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader>
            <CardDescription>Total Overdue Invoices</CardDescription>
            <CardTitle className="text-3xl text-orange-600">{report.totalOverdueInvoices}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      {/* Aging Analysis Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Bar Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Aging Analysis - Amount Distribution</CardTitle>
            <CardDescription>Overdue amount by aging bucket</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={agingChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip
                  formatter={(value: number) => formatCurrency(value)}
                  labelStyle={{ color: '#000' }}
                />
                <Legend />
                <Bar dataKey="amount" fill="#f97316" name="Amount (₹)" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Pie Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Aging Analysis - Percentage Breakdown</CardTitle>
            <CardDescription>Distribution by aging period</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={agingPieData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {agingPieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value: number) => formatCurrency(value)} />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Aging Analysis Table */}
      <Card>
        <CardHeader>
          <CardTitle>Aging Analysis Summary</CardTitle>
          <CardDescription>Detailed breakdown by aging period</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Aging Period</TableHead>
                <TableHead className="text-right">Invoice Count</TableHead>
                <TableHead className="text-right">Total Amount</TableHead>
                <TableHead className="text-right">Percentage</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {report.agingAnalysis.map((bucket, index) => (
                <TableRow key={bucket.range}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: COLORS[index % COLORS.length] }}
                      ></div>
                      {bucket.range} days
                    </div>
                  </TableCell>
                  <TableCell className="text-right">{bucket.invoiceCount}</TableCell>
                  <TableCell className="text-right font-semibold">
                    {formatCurrency(bucket.totalAmount)}
                  </TableCell>
                  <TableCell className="text-right">{bucket.percentage.toFixed(1)}%</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Customer-wise Report */}
      <Card>
        <CardHeader>
          <CardTitle>Customer-wise Overdue Summary</CardTitle>
          <CardDescription>Top customers by overdue amount</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Customer</TableHead>
                <TableHead className="text-right">Total Invoices</TableHead>
                <TableHead className="text-right">Total Overdue Amount</TableHead>
                <TableHead>Oldest Invoice</TableHead>
                <TableHead>Oldest Due Date</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {report.customerWiseReport.length > 0 ? (
                report.customerWiseReport.map((customer) => (
                  <TableRow key={customer.customerId}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{customer.customerName}</p>
                        {customer.customerEmail && (
                          <p className="text-xs text-gray-500">{customer.customerEmail}</p>
                        )}
                        {customer.customerPhone && (
                          <p className="text-xs text-gray-500">{customer.customerPhone}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">{customer.totalInvoices}</TableCell>
                    <TableCell className="text-right font-semibold text-red-600">
                      {formatCurrency(customer.totalOverdueAmount)}
                    </TableCell>
                    <TableCell>{customer.oldestInvoiceNumber}</TableCell>
                    <TableCell>{formatDate(customer.oldestDueDate)}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-gray-500">
                    No customer data available
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};
